import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Box, Layout } from '../../../components';
import { getBaseUrl } from '../../../utils';
import Button from '../../MojeId/components/Button';
import { ReactComponent as DocumentIcon } from '../../../static/images/icons/document-text.svg';
import { ReactComponent as WaitingIcon } from '../../../static/images/icons/waiting.svg';
import { ReactComponent as TimerIcon } from '../../../static/images/icons/timer.svg';
import { ReactComponent as RefreshIcon } from '../../../static/images/icons/refresh.svg';
import { ReactComponent as CheckmarkIcon } from '../../../static/images/icons/checkmark.svg';
import { Loader } from '@simplea/shared-ui';

let timer: ReturnType<typeof setTimeout> | null = null;

const countDownPeriod = 5;

interface DocumentType {
    Name: string;
    Description: string;
    ExternalId: string;
}

const BankIdSignDone = () => {
    const [searchParams] = useSearchParams();
    const [isSigned, setIsSigned] = useState(false);
    const [loading, setLoading] = useState(true);
    const [signingProcess, setSigningProcess] = useState(false);
    const [countDown, setCountDown] = useState(countDownPeriod);
    const [documents, setDocuments] = useState<DocumentType[]>([]);

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const traceId = searchParams.get('traceId');

    // useEffect(() => {
    //     axios
    //         .get(`${getBaseUrl()}/api/bank-id/upload-documents-done-callback`, {
    //             params: {
    //                 code,
    //                 state,
    //                 traceId,
    //             },
    //         })
    //         .catch((err) => console.error(err));
    // }, [code, state, traceId]);

    const detectSignedDocuments = useCallback(
        (callback?: () => void) => {
            setSigningProcess(true);
            axios
                .get(`${getBaseUrl()}/api/bank-id/get-documents-signing-status`, {
                    params: {
                        code,
                        state,
                        traceId,
                    },
                })
                .then((response) => response.data)
                .then((result) => {
                    if (result.IsSigned) {
                        setIsSigned(result.IsSigned);
                    }
                    if (result.Documents) {
                        setDocuments(result.Documents);
                    }
                })
                .catch((err) => console.error(err))
                .finally(() => {
                    callback && callback();
                    setSigningProcess(false);
                    setLoading(false);
                });
        },
        [code, state, traceId]
    );

    useEffect(() => {
        if (!isSigned) {
            timer = setTimeout(() => {
                if (countDown <= 1) {
                    detectSignedDocuments(() => {
                        setCountDown(countDownPeriod);
                    });
                } else {
                    setCountDown((prev) => prev - 1);
                }
            }, 1000);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [countDown, isSigned, detectSignedDocuments]);

    useEffect(() => {
        detectSignedDocuments();
        // eslint-disable-next-line
    }, []);

    const seconds = useMemo(() => {
        if (countDown >= 2 && countDown <= 4) return 'sekundy';
        else if (countDown === 1) return 'sekunda';
        else return 'sekund';
    }, [countDown]);

    return (
        <Layout headline="Díky, podepsáno!" description="Vaše dokumenty byly úspěšně podepsány pomocí BankID.">
            <div className="bankIdSign-wrapper">
                <Box className="white-box">
                    <div className="white-box__content small-gap">
                        {loading && (
                            <div className="text-center">
                                <Loader size="medium" />
                            </div>
                        )}
                        <div className="documents">
                            {documents.map((d) => (
                                <div key={`document-${d.ExternalId}`} className="document document-success">
                                    <div className="document__icon">
                                        <DocumentIcon />
                                    </div>
                                    <div className="document__content">
                                        <p>{d.Name}</p>
                                        <p>{d.Description}</p>
                                    </div>
                                    <div className="document__status">
                                        {isSigned ? (
                                            <span className="document-chip done">
                                                <CheckmarkIcon /> Potvrzeno
                                            </span>
                                        ) : (
                                            <span className="document-chip">
                                                <WaitingIcon /> Ověřuje se
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {!isSigned && (
                            <Fragment>
                                <div className="waiting">
                                    <TimerIcon />
                                    <p>
                                        Stav se automaticky zkontroluje za{' '}
                                        <strong>
                                            {countDown} {seconds}
                                        </strong>
                                    </p>
                                </div>
                                <div className="check-status">
                                    <Button
                                        small
                                        outlined
                                        disableHover
                                        disabled={signingProcess}
                                        iconStart={<RefreshIcon />}
                                        onClick={() => {
                                            detectSignedDocuments();
                                            setCountDown(countDownPeriod);
                                        }}
                                    >
                                        Zkontrolovat stav podpisu
                                    </Button>
                                </div>
                            </Fragment>
                        )}
                    </div>
                </Box>
                <Button small href="https://www.simplea.cz">
                    Zpět na Simplea.cz
                </Button>
            </div>
        </Layout>
    );
};

export default BankIdSignDone;
